import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, X, Clock, AlertTriangle } from "lucide-react";
import { fileUrl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authApi = (method, path, body) => {
  const token = localStorage.getItem("cps_token");
  return axios({
    method, url: `${API}${path}`, data: body,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then(r => r.data);
};

const STATUS_LBL = {
  pendente: { lbl: "Pendente", color: "#d4b27a", icon: Clock },
  aprovada: { lbl: "Aprovada", color: "#5fa86c", icon: CheckCircle2 },
  rejeitada: { lbl: "Rejeitada", color: "#c47a7a", icon: AlertTriangle },
};

/* =========================================================================
 * FOTOS PENDENTES — Fila de moderação CAMASA — rota /admin/fotos-pendentes
 * ========================================================================= */
export default function FotosPendentesPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pendente");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // série a rejeitar
  const [motivoRej, setMotivoRej] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authApi("get", `/admin/fotos-pendentes?status=${filter}`);
      setItems(r.items || []);
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const aprovar = async (serie) => {
    setActing(serie.id);
    try {
      await authApi("patch", `/admin/series_fotos/${serie.id}/aprovar`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message);
    } finally { setActing(null); }
  };

  const rejeitar = async () => {
    if (!rejectFor) return;
    setActing(rejectFor.id);
    try {
      await authApi("patch", `/admin/series_fotos/${rejectFor.id}/rejeitar`, { motivo: motivoRej });
      setRejectFor(null); setMotivoRej("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message);
    } finally { setActing(null); }
  };

  // Agrupar por obra
  const grouped = {};
  items.forEach(it => {
    const k = it.obra_id;
    if (!grouped[k]) grouped[k] = { obra_codigo: it.obra_codigo, obra_nome: it.obra_nome, cliente_nome: it.cliente_nome, items: [] };
    grouped[k].items.push(it);
  });

  return (
    <div className="container" data-testid="fotos-pendentes-panel">
      <div className="page-head fade-up">
        <div>
          <div className="eyebrow">Moderação · CAMASA</div>
          <h1 className="page-title">Fotos <em>Pendentes</em></h1>
          <p className="page-sub">Conjuntos de fotos enviados pelos parceiros aguardando sua aprovação antes de aparecer ao cliente.</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
        {[
          ["pendente", "Pendentes"], ["aprovada", "Aprovadas"], ["rejeitada", "Rejeitadas"], ["todas", "Todas"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            className={"chip " + (filter === k ? "active" : "")}
            onClick={() => setFilter(k)}
            data-testid={`fotos-filter-${k}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>Carregando…</div>
      ) : items.length === 0 ? (
        <div className="empty" data-testid="fotos-empty">
          <h3>Nenhum conjunto {filter === "pendente" ? "aguardando aprovação" : filter}</h3>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {Object.entries(grouped).map(([obraId, g]) => (
            <div key={obraId} className="panel" style={{ padding: 16 }} data-testid={`fotos-obra-${obraId}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--gold)", textTransform: "uppercase" }}>{g.obra_codigo}</div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18, color: "var(--ink-1)" }}>{g.obra_nome}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)" }}>Cliente: {g.cliente_nome || "—"}</div>
                </div>
                <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{g.items.length} conjunto(s)</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 14 }}>
                {g.items.map(s => {
                  const st = STATUS_LBL[s.status] || STATUS_LBL.pendente;
                  const StIcon = st.icon;
                  return (
                    <div key={s.id} data-testid={`fotos-card-${s.id}`} style={{ padding: 14, background: "var(--bg-3)", border: "1px solid var(--line)", borderLeft: `3px solid ${st.color}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>{s.item_descricao}</div>
                          <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2, letterSpacing: ".1em", textTransform: "uppercase" }}>
                            Etapa: {s.etapa_nome}
                          </div>
                        </div>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: st.color, padding: "3px 8px", border: `1px solid ${st.color}55`, letterSpacing: ".15em", textTransform: "uppercase" }}>
                          <StIcon size={10} /> {st.lbl}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 10 }}>
                        Por <strong style={{ color: "var(--ink-2)" }}>{s.parceiro_nome || "—"}</strong>
                        {" · "}{new Date(s.enviado_em).toLocaleString("pt-BR")}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min((s.fotos || []).length, 4)}, 1fr)`, gap: 6, marginBottom: 10 }}>
                        {(s.fotos || []).map((f, idx) => (
                          <div
                            key={idx}
                            onClick={() => setLightbox(f)}
                            data-testid={`fotos-thumb-${s.id}-${idx}`}
                            style={{
                              aspectRatio: "1/1",
                              backgroundImage: `url(${fileUrl(f.url)})`,
                              backgroundSize: "cover", backgroundPosition: "center",
                              border: "1px solid var(--line)", cursor: "zoom-in",
                              position: "relative",
                            }}
                          >
                            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "2px 4px", background: "rgba(0,0,0,0.7)", color: "white", fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", textAlign: "center" }}>
                              Imagem {idx + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Laudo: GPS + Assinatura */}
                      {(s.latitude != null || s.assinatura_base64) && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, padding: 8, marginBottom: 8, background: "rgba(184,147,90,0.05)", border: "1px solid rgba(184,147,90,0.2)" }}>
                          {s.latitude != null && (
                            <a
                              href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`fotos-gps-${s.id}`}
                              style={{ fontSize: 11, color: "var(--gold)", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
                            >
                              📍 <strong>{s.endereco_aproximado || `${s.latitude.toFixed(4)}, ${s.longitude.toFixed(4)}`}</strong>
                            </a>
                          )}
                          {s.assinatura_base64 && (
                            <div title={`Assinado por ${s.assinado_por}`} data-testid={`fotos-sig-${s.id}`} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <img
                                src={s.assinatura_base64}
                                alt="assinatura"
                                style={{ height: 28, maxWidth: 84, background: "white", border: "1px solid var(--line)", padding: 2 }}
                              />
                              <span style={{ fontSize: 9, color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase" }}>{s.assinado_por || "—"}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {s.status === "rejeitada" && s.motivo_rejeicao && (
                        <div style={{ fontSize: 11, padding: 8, marginBottom: 8, background: "rgba(196,122,122,0.08)", color: "#d49595", border: "1px solid rgba(196,122,122,0.3)" }}>
                          <strong>Rejeitada:</strong> {s.motivo_rejeicao}
                        </div>
                      )}
                      {s.status === "pendente" && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className="btn btn-sm"
                            onClick={() => aprovar(s)}
                            disabled={acting === s.id}
                            data-testid={`fotos-aprovar-${s.id}`}
                            style={{ flex: 1, background: "var(--verde-2, #3a5a4a)", color: "#fff", fontWeight: 600 }}
                          >
                            <CheckCircle2 size={12} /> {acting === s.id ? "…" : "Aprovar"}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setRejectFor(s)}
                            disabled={acting === s.id}
                            data-testid={`fotos-rejeitar-${s.id}`}
                            style={{ flex: 1, color: "#c47a7a", borderColor: "#4a2a2a" }}
                          >
                            <X size={12} /> Rejeitar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {rejectFor && (
        <div className="modal-back" onClick={() => setRejectFor(null)} data-testid="reject-modal">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-head">
              <h3 className="modal-title">Rejeitar série</h3>
              <button className="modal-close" onClick={() => setRejectFor(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 12 }}>
                <strong>{rejectFor.item_descricao}</strong> — {rejectFor.etapa_nome}
              </p>
              <div className="field">
                <label>Motivo da rejeição (opcional)</label>
                <textarea
                  className="input" rows={4}
                  value={motivoRej}
                  onChange={e => setMotivoRej(e.target.value.slice(0, 400))}
                  placeholder="Ex: Imagem 1 está fora de foco. Refaça a foto."
                  data-testid="reject-motivo"
                />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => { setRejectFor(null); setMotivoRej(""); }}>Cancelar</button>
              <button
                className="btn"
                onClick={rejeitar}
                disabled={acting === rejectFor.id}
                data-testid="reject-confirm"
                style={{ background: "#c47a7a", color: "#fff" }}
              >
                {acting === rejectFor.id ? "Rejeitando…" : "Confirmar rejeição"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)} data-testid="fotos-lightbox">
          <button className="lightbox-close" onClick={() => setLightbox(null)}>×</button>
          <img src={fileUrl(lightbox.url)} alt={lightbox.angulo} />
          <div className="lightbox-caption">{lightbox.angulo}</div>
        </div>
      )}
    </div>
  );
}
